import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCheck, LuChevronDown, LuSearch, LuX } from 'react-icons/lu';
import Popover from '../ui/Popover';
import { GLASS_CHIP, GLASS_FIELD } from '../ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import { ERROR_RING } from './FormControls';

/**
 * Multi-select with a search box, in the glass field style. The page keeps
 * the selection: `isSelected(option)` and `onToggle(option)` (so an option
 * may stand for several stored ids). `summary` is the trigger text.
 * options: [{ key, label, hint?, badge?, muted? }]
 */
const MultiSelect = ({ id, options, isSelected, onToggle, summary, placeholder, searchPlaceholder, emptyText, warning, matches }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const triggerRef = useRef(null);
  const searchLower = searchTerm.toLocaleLowerCase('tr-TR');
  const visible = options.filter((option) => (matches
    ? matches(option, searchLower)
    : String(option.label).toLocaleLowerCase('tr-TR').includes(searchLower)));

  const close = () => {
    setOpen(false);
    setSearchTerm('');
  };

  return (
    <>
      <button
        ref={triggerRef}
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => (open ? close() : setOpen(true))}
        className={`tw-flex tw-h-10 tw-w-full tw-items-center tw-justify-between tw-gap-2 tw-rounded-xl tw-px-3.5 tw-text-left tw-text-base tw-transition-shadow focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/40 sm:tw-text-sm ${GLASS_CHIP} ${warning ? ERROR_RING : ''}`}
      >
        <span className={`tw-min-w-0 tw-truncate ${summary ? 'tw-text-slate-800 dark:tw-text-slate-100' : 'tw-text-slate-400 dark:tw-text-slate-500'}`}>
          {summary || placeholder}
        </span>
        <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400 tw-transition-transform ${open ? 'tw-rotate-180' : ''}`} />
      </button>

      <Popover anchorRef={triggerRef} open={open} onClose={close} label={placeholder} role="dialog" className="tw-w-[min(26rem,calc(100vw-1.5rem))] tw-p-2">
        <div className="tw-relative tw-mb-2">
          <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
          <input
            data-autofocus
            type="text"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            className={`${GLASS_FIELD} tw-pl-10 tw-pr-9`}
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm('')}
              aria-label={t('dataTable.search.clear')}
              className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
            >
              <LuX aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
            </button>
          )}
        </div>
        <ul role="listbox" aria-multiselectable="true" className="tw-max-h-64 tw-space-y-0.5 tw-overflow-y-auto tw-overscroll-contain glass-scrollbar">
          {visible.length === 0 ? (
            <li className="tw-px-3 tw-py-2 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{searchTerm ? t('dataTable.form.noOptions') : emptyText || t('dataTable.form.noOptions')}</li>
          ) : visible.map((option) => {
            const selected = isSelected(option);
            return (
              <li key={option.key} className="tw-block">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => onToggle(option)}
                  className={`tw-flex tw-w-full tw-items-center tw-gap-2.5 tw-rounded-xl tw-bg-transparent tw-px-2.5 tw-py-2 tw-text-left tw-text-sm tw-transition-colors hover:tw-bg-slate-900/[0.05] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:hover:tw-bg-white/[0.07] ${option.muted ? 'tw-opacity-70' : ''}`}
                >
                  <span
                    aria-hidden="true"
                    className={`tw-grid tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-place-items-center tw-rounded-md tw-border tw-transition-colors ${
                      selected
                        ? 'tw-border-teal-500 tw-bg-teal-500 tw-text-white dark:tw-border-teal-400'
                        : 'tw-border-slate-300 tw-bg-white/60 dark:tw-border-white/20 dark:tw-bg-white/[0.04]'
                    }`}
                  >
                    {selected && <LuCheck className="tw-h-3 tw-w-3" strokeWidth={3} />}
                  </span>
                  <span className="tw-min-w-0 tw-flex-1">
                    <span className={`tw-block tw-truncate ${selected ? 'tw-font-semibold tw-text-slate-900 dark:tw-text-white' : 'tw-font-medium tw-text-slate-700 dark:tw-text-slate-200'}`}>{option.label}</span>
                    {option.hint && <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{option.hint}</span>}
                  </span>
                  {option.badge}
                </button>
              </li>
            );
          })}
        </ul>
      </Popover>
    </>
  );
};

MultiSelect.propTypes = {
  id: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.node.isRequired,
    hint: PropTypes.node,
    badge: PropTypes.node,
    muted: PropTypes.bool,
  })).isRequired,
  isSelected: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  summary: PropTypes.string,
  placeholder: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  emptyText: PropTypes.string,
  warning: PropTypes.bool,
  matches: PropTypes.func,
};

export default MultiSelect;
